import { useState, useEffect } from 'react';
import { ExternalLink, Share2, MessageSquare, Bell, BellRing } from 'lucide-react';
import { API, BACKEND_URL, authFetch } from '@/App';
import { toast } from 'sonner';

export default function ChecklistView({ checklist, compact = false, showShare = false, onShareInChat }) {
  const [products, setProducts] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [alertedProducts, setAlertedProducts] = useState(new Set());

  const appendUtm = (url, product) => {
    if (!url) return '#';
    try {
      const u = new URL(url);
      u.searchParams.set('utm_source', 'gloverse');
      u.searchParams.set('utm_medium', 'checklist');
      u.searchParams.set('utm_campaign', checklist?.checklist_id || 'direct');
      u.searchParams.set('utm_content', product?.product_id || '');
      return u.toString();
    } catch {
      return url;
    }
  };

  const trackClick = async (product) => {
    try {
      await authFetch(`${API}/analytics/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.product_id,
          product_name: product.name,
          brand: product.brand,
          checklist_id: checklist?.checklist_id || null,
          checklist_owner_id: checklist?.user_id || null,
          purchase_url: product.purchase_url,
          source: 'checklist'
        })
      });
    } catch { /* silent */ }
  };

  const handleProductClick = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const url = product.purchase_url ? appendUtm(product.purchase_url, product) : null;
    if (!url || url === '#') {
      toast.error('Purchase link not available for this product');
      return;
    }
    trackClick(product);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    // Guard against null/undefined checklist
    if (!checklist) return;
    
    // If product_details are already provided (from the new API), use them
    if (checklist.product_details && checklist.product_details.length > 0) {
      setProducts(checklist.product_details);
    } else if (checklist.products && checklist.products.length > 0) {
      fetchProducts();
    }
    fetchAlerts();
  }, [checklist]);

  const fetchProducts = async () => {
    try {
      const productPromises = checklist.products.map(productId =>
        authFetch(`${API}/products?product_id=${productId}`).then(r => r.json())
      );
      const productArrays = await Promise.all(productPromises);
      const flat = productArrays.flat();
      // Preserve the order from checklist.products
      const productsMap = new Map(flat.map(p => [p.product_id, p]));
      const ordered = checklist.products
        .map(id => productsMap.get(id))
        .filter(Boolean);
      setProducts(ordered);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await authFetch(`${API}/price-alerts`);
      if (response.ok) {
        const alerts = await response.json();
        setAlertedProducts(new Set(alerts.map(a => a.product_id)));
      }
    } catch (error) {
      // Silent - user may not be logged in
    }
  };

  const togglePriceAlert = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await authFetch(`${API}/products/${productId}/toggle-alert`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setAlertedProducts(prev => {
          const next = new Set(prev);
          if (data.subscribed) next.add(productId);
          else next.delete(productId);
          return next;
        });
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to toggle price alert');
    }
  };

  const trackShare = async (medium) => {
    try {
      await authFetch(`${API}/analytics/track-share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist_id: checklist.checklist_id, medium })
      });
    } catch (e) { /* silent */ }
  };

  const getShareUrl = () => {
    return `https://gloverse.in/shared/checklist/${checklist.checklist_id}`;
  };

  const getGloverseLink = () => 'https://gloverse.in/login';

  const buildShareText = () => {
    const productLines = products.map((p, i) => {
      return `${i + 1}. ${p.name} by ${p.brand}${p.price ? ` - ${p.price}` : ''}`;
    }).join('\n');
    return `${checklist.name} on Gloverse\n\n${productLines}\n\nCheck it out: ${getShareUrl()}\nJoin Gloverse: ${getGloverseLink()}`;
  };

  const handleNativeShare = async () => {
    const shareText = buildShareText();
    const shareUrl = getShareUrl();
    try {
      await navigator.share({
        title: `${checklist.name} — Gloverse`,
        text: shareText,
        url: shareUrl,
      });
      trackShare('native');
      setShowShareMenu(false);
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error('Sharing failed');
      }
    }
  };

  const handleShare = (platform) => {
    const shareUrl = getShareUrl();
    const shareText = buildShareText();
    
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this beauty checklist on Gloverse!`)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
      setShowShareMenu(false);
      trackShare(platform);
    }
  };

  const handleInstagramShare = () => {
    const shareText = `${checklist.name} on Gloverse\n\n${products.map((p, i) => `${i + 1}. ${p.name} by ${p.brand}`).join('\n')}\n\nJoin Gloverse: ${getGloverseLink()}`;
    navigator.clipboard.writeText(shareText);
    trackShare('instagram');
    toast.success('Checklist copied! Paste it in your Instagram Story or DM');
    setShowShareMenu(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    trackShare('copy_link');
    toast.success('Link copied to clipboard!');
    setShowShareMenu(false);
  };

  const handleShareAsImage = async () => {
    setShowShareMenu(false);
    toast.info('Generating card image...');
    try {
      const cardUrl = `${API}/checklists/${checklist.checklist_id}/card`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      const blob = await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
        };
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = cardUrl;
      });

      const file = new File([blob], `${checklist.name.replace(/\s+/g, '_')}_gloverse.png`, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${checklist.name} — Gloverse`,
          text: `Check out my beauty checklist on Gloverse!\nJoin here: ${getGloverseLink()}`,
          files: [file]
        });
        trackShare('image_native');
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        trackShare('image_download');
        toast.success('Card image downloaded!');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        try {
          const a = document.createElement('a');
          a.href = `${API}/checklists/${checklist.checklist_id}/card`;
          a.download = `${checklist.name.replace(/\s+/g, '_')}_gloverse.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          trackShare('image_download');
          toast.success('Card image downloading...');
        } catch {
          toast.error('Failed to generate card image');
        }
      }
    }
  };

  const handleDownloadImage = async () => {
    setShowShareMenu(false);
    toast.info('Downloading card image...');
    try {
      const cardUrl = `${API}/checklists/${checklist.checklist_id}/card`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      const blob = await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
        };
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = cardUrl;
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${checklist.name.replace(/\s+/g, '_')}_gloverse.png`;
      a.click();
      URL.revokeObjectURL(url);
      trackShare('image_download');
      toast.success('Card image downloaded!');
    } catch (e) {
      try {
        const a = document.createElement('a');
        a.href = `${API}/checklists/${checklist.checklist_id}/card`;
        a.download = `${checklist.name.replace(/\s+/g, '_')}_gloverse.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        trackShare('image_download');
        toast.success('Card image downloading...');
      } catch {
        toast.error('Failed to download card image');
      }
    }
  };

  const handleShareInChat = () => {
    if (onShareInChat) {
      onShareInChat(checklist);
      trackShare('in_app_chat');
    }
    setShowShareMenu(false);
  };

  // Guard against null/undefined checklist
  if (!checklist) {
    return (
      <div className="checklist-card rounded-xl p-4">
        <p className="text-sm text-muted-foreground">No checklist available</p>
      </div>
    );
  }

  return (
    <div className="checklist-card rounded-xl p-4" data-testid={`checklist-${checklist?.checklist_id}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(330, 85%, 50%)' }}>
          {checklist?.name || 'Checklist'}
        </h3>
        <div className="flex items-center gap-2">
          {checklist.filters?.skin_type && (
            <span className="px-2 py-1 bg-white/70 rounded-full text-xs font-medium text-muted-foreground">
              {checklist.filters.skin_type}
            </span>
          )}
          {checklist.filters?.category && (
            <span className="px-2 py-1 bg-white/70 rounded-full text-xs font-medium text-muted-foreground">
              {checklist.filters.category}
            </span>
          )}
          {showShare && (
            <div className="relative">
              <button
                data-testid="share-checklist-btn"
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-2 hover:bg-white/90 rounded-full transition-colors"
              >
                <Share2 className="w-5 h-5 text-primary" />
              </button>
              {showShareMenu && (
                <div className="absolute right-0 top-12 bg-white rounded-xl shadow-lg border border-border p-2 z-50 min-w-[220px]">
                  <button
                    data-testid="share-as-image"
                    onClick={handleShareAsImage}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-pink-50 rounded-lg transition-colors text-left"
                  >
                    <svg className="w-5 h-5 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                    <div>
                      <span className="font-semibold text-sm block">Share as Image</span>
                      <span className="text-[11px] text-muted-foreground">Perfect for Instagram & WhatsApp</span>
                    </div>
                  </button>
                  <button
                    data-testid="download-image"
                    onClick={handleDownloadImage}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-pink-50 rounded-lg transition-colors text-left border-b border-border mb-1 pb-2"
                  >
                    <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <div>
                      <span className="font-semibold text-sm block">Download Image</span>
                      <span className="text-[11px] text-muted-foreground">Save card to your device</span>
                    </div>
                  </button>
                  <button
                    data-testid="share-whatsapp"
                    onClick={() => handleShare('whatsapp')}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-primary/5 rounded-lg transition-colors text-left"
                  >
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span className="font-medium">WhatsApp</span>
                  </button>
                  <button
                    data-testid="share-instagram"
                    onClick={handleInstagramShare}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-primary/5 rounded-lg transition-colors text-left"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <defs>
                        <linearGradient id="ig-grad" x1="0" y1="1" x2="1" y2="0">
                          <stop offset="0%" stopColor="#feda75"/>
                          <stop offset="25%" stopColor="#fa7e1e"/>
                          <stop offset="50%" stopColor="#d62976"/>
                          <stop offset="75%" stopColor="#962fbf"/>
                          <stop offset="100%" stopColor="#4f5bd5"/>
                        </linearGradient>
                      </defs>
                      <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig-grad)" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="12" r="4.5" stroke="url(#ig-grad)" strokeWidth="2" fill="none"/>
                      <circle cx="17.5" cy="6.5" r="1.25" fill="url(#ig-grad)"/>
                    </svg>
                    <span className="font-medium">Instagram</span>
                  </button>
                  <button
                    data-testid="share-copy-link"
                    onClick={copyLink}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-primary/5 rounded-lg transition-colors text-left border-t border-border mt-1 pt-2"
                  >
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span className="font-medium">Copy Link</span>
                  </button>
                  {onShareInChat && (
                    <button
                      data-testid="share-checklist-in-chat"
                      onClick={handleShareInChat}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-primary/5 rounded-lg transition-colors text-left"
                    >
                      <MessageSquare className="w-5 h-5 text-primary" />
                      <span className="font-medium">Share in Chat</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading products...</p>
        ) : (
          products.map(product => (
            <div key={product.product_id} className="flex items-center gap-3 p-3 bg-white/70 rounded-lg hover:bg-white hover:shadow-sm transition-all group">
              <a
                href={appendUtm(product.purchase_url, product)}
                onClick={(e) => handleProductClick(e, product)}
                data-testid={`checklist-product-${product.product_id}`}
                className="flex items-center gap-3 flex-1 min-w-0"
              >
                {!compact && (
                  product.image ? (
                    <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-200 to-pink-100 flex items-center justify-center text-purple-500 font-bold text-lg shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      G
                    </div>
                  )
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors truncate">
                        {product.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">{product.brand}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-1" />
                  </div>
                  <p className="text-sm font-bold text-primary mt-1">{product.price}</p>
                </div>
              </a>
              <button
                data-testid={`price-alert-${product.product_id}`}
                onClick={(e) => togglePriceAlert(e, product.product_id)}
                className={`p-2 rounded-full transition-all shrink-0 ${
                  alertedProducts.has(product.product_id)
                    ? 'bg-amber-50 text-amber-500 hover:bg-amber-100'
                    : 'text-muted-foreground/50 hover:text-amber-500 hover:bg-amber-50'
                }`}
                title={alertedProducts.has(product.product_id) ? 'Price alert active' : 'Set price alert'}
              >
                {alertedProducts.has(product.product_id)
                  ? <BellRing className="w-4 h-4" />
                  : <Bell className="w-4 h-4" />
                }
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
